import React, { lazy, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import Loadable from '@ui/components/Loadable';
import { ProtectedRoute } from './components/ProtectedRoute';
import { CountryServiceRoute } from './components/CountryServiceRoute';
import ExternalRedirect from './components/ExternalRedirect';
import { useTracking } from '@ui/hooks/useTracking';
import { ReferralContextProvider } from './hooks/useReferral';
import { GuestPropertyFinderSessionContextProvider } from './hooks/useGuestPropertyFinderSession';
import { PropertyFinderSessionsContextProvider } from './hooks/usePropertyFinderSessions';
import { PropertyFinderFavouritesContextProvider } from './hooks/usePropertyFinderFavourites';

// External WordPress marketing pages
const EXTERNAL_URLS = {
  PROPERTY_FINDER: 'https://upscoreapp.com/property-finder',
  PROPERTY_FINDER_ES: 'https://upscoreapp.com/property-finder-es/',
  PROPERTY_FINDER_AE: 'https://upscoreapp.com/property-finder-ae/',
  PROPERTY_FINDER_PT: 'https://upscoreapp.com/property-finder-pt/'
};

const Register = Loadable(lazy(() => import('./pages/account/Register')));
const Login = Loadable(lazy(() => import('./pages/account/Login')));
const PasswordRecover = Loadable(lazy(() => import('./pages/account/PasswordRecover')));
const PasswordRecoverConfirm = Loadable(
  lazy(() => import('./pages/account/PasswordRecoverConfirm'))
);
const RegisterConfirm = Loadable(lazy(() => import('./pages/account/RegisterConfirm')));
const MoreRegisterConfirmOptions = Loadable(
  lazy(() => import('./pages/account/MoreRegisterConfirmOptions'))
);
const RegisterChangeEmailConfirmOptions = Loadable(
  lazy(() => import('./pages/account/RegisterChangeEmailConfirmOptions'))
);
const RegisterChangePhoneConfirmOptions = Loadable(
  lazy(() => import('./pages/account/RegisterChangePhoneConfirmOptions'))
);

const Home = Loadable(lazy(() => import('./pages/home/Home')));
const Referrals = Loadable(lazy(() => import('./pages/referrals/Referrals')));
const ReferralsWithdrawal = Loadable(lazy(() => import('./pages/referrals/ReferralsWithdrawal')));
const FinancePassportTMLandingPageView = Loadable(
  lazy(() => import('./pages/passport/FinancePassportTMLandingPageView'))
);
const GuestPropertyFinderLandingPage = Loadable(
  lazy(() => import('./pages/propertyFinder/GuestPropertyFinderLandingPageView'))
);
const GuestPropertyFinderSessionDetails = Loadable(
  lazy(() => import('./pages/propertyFinder/GuestPropertyFinderSessionDetails'))
);
const PropertyFinderEmbed = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderEmbed'))
);
const PropertyFinderAutoSearch = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderAutoSearch'))
);
const PropertyFinderSessionsListView = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderSessionsListView'))
);
const PropertyFinderSessionDetails = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderSessionDetails'))
);
const PropertyFinderSessionCreate = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderSessionCreate'))
);
const PropertyFinderFavouritesListView = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderFavouritesListView'))
);
const PropertyFinderContactAgents = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderContactAgents'))
);
const PropertyFinderContactAgentsScheduled = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderContactAgentsScheduled'))
);
const PropertyFinderScheduledViewings = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderScheduledViewings'))
);
const PropertyFinderRegisterScreen = Loadable(
  lazy(() => import('./pages/propertyFinder/PropertyFinderRegister'))
);
const PassportOnboardingLandingPageView = Loadable(
  lazy(() => import('./pages/passport/PassportOnboardingLandingPageView'))
);
const PassportOnboardingDetails = Loadable(
  lazy(() => import('./pages/passport/PassportOnboardingDetails'))
);
const PassportOnboardingResume = Loadable(
  lazy(() => import('./pages/passport/PassportOnboardingResume'))
);
const PassportOnboardingDocumentsManagement = Loadable(
  lazy(() => import('./pages/passport/PassportOnboardingDocumentsManagement'))
);
const PassportOnboardingThankYouPage = Loadable(
  lazy(() => import('./pages/passport/PassportOnboardingThankYouPage'))
);
const Passport = Loadable(lazy(() => import('./pages/passport/Passport')));
const PassportRequestView = Loadable(lazy(() => import('./pages/passport/PassportRequestView')));
const PassportDocumentsView = Loadable(
  lazy(() => import('./pages/passport/PassportDocumentsView'))
);
const PassportDetails = Loadable(lazy(() => import('./pages/passport/PassportDetails')));
const PassportRequest = Loadable(lazy(() => import('./pages/passport/PassportRequest')));
const PassportDocuments = Loadable(lazy(() => import('./pages/passport/PassportDocuments')));
const MortgageBookCall = Loadable(lazy(() => import('./pages/passport/MortgageBookCall')));
const Notifications = Loadable(lazy(() => import('./pages/notifications/Notifications')));
const Settings = Loadable(lazy(() => import('./pages/settings/Settings')));
const SettingsRemove = Loadable(lazy(() => import('./pages/settings/RemoveAccount')));
const SettingsPersonalInformation = Loadable(
  lazy(() => import('./pages/settings/PersonalInformation'))
);
const SettingsBankDetails = Loadable(lazy(() => import('./pages/settings/BankDetails')));
const SettingsBankDetailsSubmit = Loadable(
  lazy(() => import('./pages/settings/BankDetailsSubmit'))
);
const SettingsHelp = Loadable(lazy(() => import('./pages/settings/Help')));
const SettingsMortgageApplication = Loadable(
  lazy(() => import('./pages/settings/MortgageApplication'))
);
const SettingsOtherCountries = Loadable(lazy(() => import('./pages/settings/OtherCountries')));
const SettingsReferrals = Loadable(lazy(() => import('./pages/settings/SettingsReferrals')));
const SettingsNotifications = Loadable(
  lazy(() => import('./pages/settings/SettingsNotifications'))
);
const ProfileSetupRegisterIntroFlow = Loadable(
  lazy(() => import('./pages/profile/RegisterFlows/OnboardingFlow'))
);
const CurrencyExchange = Loadable(lazy(() => import('./pages/currencies/CurrencyConverter')));
const CurrencyConverterOnboarding = Loadable(
  lazy(() => import('./pages/currencies/CurrencyConverterOnboarding'))
);
const WiseWidgetEmbed = Loadable(lazy(() => import('./pages/currencies/WiseWidgetEmbed')));
const WiseAccountVerification = Loadable(lazy(() => import('./pages/wiseAccountVerification')));
const Calculators = Loadable(lazy(() => import('./pages/calculators/Calculators')));
const SellerIntake = Loadable(lazy(() => import('./pages/seller-intake')));
const EmailFollowupBookingPage = Loadable(lazy(() => import('./pages/booking')));
const CountryServicesOnboarding = Loadable(
  lazy(() => import('./pages/country-services/onboarding'))
);
const CountryServicesDocuments = Loadable(
  lazy(() => import('./pages/country-services/documents-upload-page'))
);
const AppCountryServices = Loadable(lazy(() => import('./pages/country-services')));
const CountryServicesBookCall = Loadable(lazy(() => import('./pages/country-services/book-call')));

function RoutesComponent() {
  const location = useLocation();
  const { trackPage } = useTracking();

  useEffect(() => {
    trackPage({
      data: location?.pathname
    });
  }, [location, trackPage]);

  return (
    <Routes>
      <Route path="/register" element={<Register />} />
      <Route path="/register/:email" element={<Register />} />
      <Route path="/register/confirm" element={<RegisterConfirm />} />
      <Route path="/register/confirm/:email" element={<RegisterConfirm />} />
      <Route path="/register/confirm/:email/:code" element={<RegisterConfirm />} />
      <Route
        path="/register/confirm/more-options/:email"
        element={<MoreRegisterConfirmOptions />}
      />
      <Route
        path="/register/confirm/more-options/:email/email"
        element={<RegisterChangeEmailConfirmOptions />}
      />
      <Route
        path="/register/confirm/more-options/:email/phone"
        element={<RegisterChangePhoneConfirmOptions />}
      />
      <Route path="/login" element={<Login />} />
      <Route path="/login/:email" element={<Login />} />
      <Route path="/password/recover" element={<PasswordRecover />} />
      <Route path="/password/recover/:email" element={<PasswordRecover />} />
      <Route path="/password/recover/confirm/:email" element={<PasswordRecoverConfirm />} />
      <Route path="/password/recover/confirm/:email/:code" element={<PasswordRecoverConfirm />} />

      <Route path="/finance-passport-tm" element={<FinancePassportTMLandingPageView />} />

      {/* Property Finder external redirects to WordPress marketing pages */}
      <Route
        path="/property-finder"
        element={<ExternalRedirect to={EXTERNAL_URLS.PROPERTY_FINDER} />}
      />
      <Route
        path="/property-finder/c/es"
        element={<ExternalRedirect to={EXTERNAL_URLS.PROPERTY_FINDER_ES} />}
      />
      <Route
        path="/property-finder/c/ae"
        element={<ExternalRedirect to={EXTERNAL_URLS.PROPERTY_FINDER_AE} />}
      />
      <Route
        path="/property-finder/c/pt"
        element={<ExternalRedirect to={EXTERNAL_URLS.PROPERTY_FINDER_PT} />}
      />
      {/* Catch-all for other countries (e.g., /property-finder/c/fr) */}
      <Route
        path="/property-finder/c/:country"
        element={
          <GuestPropertyFinderSessionContextProvider>
            <GuestPropertyFinderLandingPage />
          </GuestPropertyFinderSessionContextProvider>
        }
      />
      <Route
        path="/property-finder/results/:sessionId"
        element={
          <GuestPropertyFinderSessionContextProvider>
            <GuestPropertyFinderSessionDetails />
          </GuestPropertyFinderSessionContextProvider>
        }
      />
      <Route path="/property-finder/auto-search" element={<PropertyFinderAutoSearch />} />
      <Route path="/embed/property-finder" element={<PropertyFinderEmbed />} />
      <Route path="/embed/property-finder/c/:country" element={<PropertyFinderEmbed />} />
      <Route path="/property-finder-register" element={<PropertyFinderRegisterScreen />} />
      <Route path="/property-finder-register/:email" element={<PropertyFinderRegisterScreen />} />
      <Route
        path="/app/property-finder/past-searches"
        element={
          <ProtectedRoute>
            <PropertyFinderSessionsContextProvider>
              <PropertyFinderSessionsListView />
            </PropertyFinderSessionsContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/property-finder/favourites"
        element={
          <ProtectedRoute>
            <PropertyFinderFavouritesContextProvider>
              <PropertyFinderFavouritesListView />
            </PropertyFinderFavouritesContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/property-finder/contact-agents"
        element={
          <ProtectedRoute>
            <PropertyFinderSessionsContextProvider>
              <PropertyFinderContactAgents />
            </PropertyFinderSessionsContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/property-finder/contact-agents-scheduled"
        element={
          <ProtectedRoute>
            <PropertyFinderSessionsContextProvider>
              <PropertyFinderContactAgentsScheduled />
            </PropertyFinderSessionsContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/property-finder/scheduled-viewings"
        element={
          <ProtectedRoute>
            <PropertyFinderSessionsContextProvider>
              <PropertyFinderScheduledViewings />
            </PropertyFinderSessionsContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/property-finder/past-searches/add"
        element={
          <ProtectedRoute>
            <PropertyFinderSessionsContextProvider>
              <GuestPropertyFinderSessionContextProvider>
                <PropertyFinderSessionCreate />
              </GuestPropertyFinderSessionContextProvider>
            </PropertyFinderSessionsContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/property-finder/past-searches/:sessionId"
        element={
          <ProtectedRoute>
            <PropertyFinderSessionsContextProvider>
              <GuestPropertyFinderSessionContextProvider>
                <PropertyFinderFavouritesContextProvider>
                  <PropertyFinderSessionDetails />
                </PropertyFinderFavouritesContextProvider>
              </GuestPropertyFinderSessionContextProvider>
            </PropertyFinderSessionsContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance-passport-tm/:referralCode"
        element={<FinancePassportTMLandingPageView />}
      />
      <Route
        path="/onboarding/passport/:countryCode"
        element={<PassportOnboardingLandingPageView />}
      />
      <Route
        path="/onboarding/passport/:countryCode/stage-2"
        element={<PassportOnboardingDetails />}
      />
      <Route path="/onboarding/passport/resume" element={<PassportOnboardingResume />} />
      <Route
        path="/onboarding/passport/documents/:authCode"
        element={<PassportOnboardingDocumentsManagement />}
      />
      <Route
        path="/onboarding/passport/:countryCode/thank-you/:email"
        element={<PassportOnboardingThankYouPage />}
      />

      <Route
        path="/"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <Home />
          </ProtectedRoute>
        }
      />

      <Route
        path="/passport"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <Passport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport/:country_code"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <PassportRequestView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport/other-countries/:country_code"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <PassportRequestView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport/documents"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <PassportDocumentsView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/verify-euro-account"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <WiseAccountVerification />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport/details"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <PassportDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport/request"
        element={
          <ProtectedRoute>
            <PassportRequest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport/request/:country_code"
        element={
          <ProtectedRoute>
            <PassportRequest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport/request/update"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <PassportRequest updatePassportMode />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passport/documents/:authCode"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <PassportDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/passport/book-mortgage-call"
        element={
          <ProtectedRoute requiredProfileCompleted allowSkippedToBookCall>
            <MortgageBookCall />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/remove"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsRemove />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/personal-information"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsPersonalInformation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/bank-details"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsBankDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/bank-details/submit"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsBankDetailsSubmit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/mortgage-application"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsMortgageApplication />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/help"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsHelp />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/other-countries"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsOtherCountries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/referrals"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <ReferralContextProvider>
              <SettingsReferrals />
            </ReferralContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <SettingsNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/intro"
        element={
          <ProtectedRoute>
            <ProfileSetupRegisterIntroFlow />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <ReferralContextProvider>
              <Referrals />
            </ReferralContextProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals/withdrawal"
        element={
          <ProtectedRoute requiredProfileCompleted>
            <ReferralContextProvider>
              <ReferralsWithdrawal />
            </ReferralContextProvider>
          </ProtectedRoute>
        }
      />
      <Route path="/r/:refId" element={<Register />} />
      <Route
        path="/p/:referralCode/:partnerPeferralCode"
        element={<FinancePassportTMLandingPageView />}
      />
      <Route
        path="/fx"
        element={
          <ProtectedRoute>
            <CurrencyExchange />
          </ProtectedRoute>
        }
      />
      <Route path="/onboarding/fx/:authCode" element={<CurrencyConverterOnboarding />} />
      <Route path="/calculators/:id" element={<Calculators />} />
      <Route path="/embed/wise" element={<WiseWidgetEmbed />} />

      {/* Email follow-up booking page (public, no auth) */}
      <Route
        path="/:countryCode/booking/:passportRequestId"
        element={<EmailFollowupBookingPage />}
      />

      {/* Seller Intake Routes */}
      <Route path="/:countryCode/seller-intake/*" element={<SellerIntake />} />

      {/* Country Services Routes - Public Onboarding */}
      <Route
        path="/onboarding/country-services/:countryCode/*"
        element={<CountryServicesOnboarding />}
      />

      {/* Country Services Routes - Public Documents Upload */}
      <Route
        path="/country-services/:countryCode/documents/:authCode"
        element={<CountryServicesDocuments />}
      />

      {/* Country Services Routes - Authenticated App Section */}
      <Route
        path="/country-services/:countryCode"
        element={
          <ProtectedRoute>
            <CountryServiceRoute>
              <AppCountryServices />
            </CountryServiceRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/country-services/:countryCode/book-call"
        element={
          <ProtectedRoute>
            <CountryServiceRoute>
              <CountryServicesBookCall />
            </CountryServiceRoute>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default RoutesComponent;
